import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CollectionCard from "@/components/collections/CollectionCard";
import CollectionEmptyState from "@/components/collections/CollectionEmptyState";
import CollectionErrorState from "@/components/collections/CollectionErrorState";
import CollectionLoadingState from "@/components/collections/CollectionLoadingState";
import { useBaseTierCollections } from "@/hooks/useBaseTierCollections";
import { SORT_OPTIONS, type SortOption } from "@/types/types";

// ─── Page ─────────────────────────────────────────────────────────────────────
// One card per budget tier (base collection image + display name). Clicking a tier opens its
// composed product feed at /collections/tier/:budget.

export default function Collections() {
  const navigate = useNavigate();
  const [sortOrder, setSortOrder] = useState<SortOption>("price-low-high");

  const { data: tiers = [], isLoading, isError } = useBaseTierCollections();

  const sorted = useMemo(
    () =>
      [...tiers].sort((a, b) =>
        sortOrder === "price-low-high" ? a.budget - b.budget : b.budget - a.budget,
      ),
    [tiers, sortOrder],
  );

  if (isLoading) return <CollectionLoadingState />;
  if (isError) return <CollectionErrorState />;

  return (
    <Box
      sx={{
        backgroundColor: "var(--color-white)",
        borderRadius: "12px",
        border: "1px solid var(--color-smoke)",
        p: 3,
      }}
    >
      {/* Toolbar ─ sort by budget */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "var(--color-dark-dust)",
              whiteSpace: "nowrap",
            }}
          >
            Sort by:
          </Typography>
          <Select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as SortOption)}
            size="small"
            sx={{
              fontSize: "14px",
              height: "40px",
              minWidth: "180px",
              borderRadius: "6px",
              backgroundColor: "white",
              color: "var(--color-graphite)",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--color-rodeo-dust)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--color-primary-active)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--color-primary-active)",
              },
            }}
          >
            {SORT_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                sx={{ fontSize: "14px", color: "var(--color-graphite)" }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Tier grid */}
      {sorted.length === 0 ? (
        <CollectionEmptyState />
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(275px, 1fr))",
            gap: 3,
          }}
        >
          {sorted.map((tier) => (
            <CollectionCard
              key={tier.budget}
              collection={{ title: tier.title, imageUrl: tier.imageUrl }}
              onClick={() => navigate(`/collections/tier/${tier.budget}`)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
